import { useEffect, useState, useRef } from "react";
import { useOrg } from "@/context/OrgContext";
import { useSearchParams } from "react-router-dom";
import { api, getErrorMessage } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Building2, Users, UserPlus, Mail, ShieldCheck, Crown, X, Plus,
  CreditCard, Trash2, Copy, Loader2, Settings, Send, Link2, HardHat,
} from "lucide-react";
import PermissionsEditorDialog from "@/components/PermissionsEditorDialog";
import { useLockedAccounts, LockedBadge, UnlockButton } from "@/components/security/LockedAccounts";

const PlanBadge = ({ plan }) => {
  const labels = { trial: "Essai 30j", starter: "Starter", enterprise: "Enterprise", unlimited: "Gratuit illimité" };
  const colors = {
    trial: "bg-amber-100 text-amber-800 border-amber-300",
    starter: "bg-indigo-100 text-indigo-800 border-indigo-300",
    enterprise: "bg-purple-100 text-purple-800 border-purple-300",
    unlimited: "bg-emerald-100 text-emerald-800 border-emerald-300",
  };
  return (
    <Badge className={`${colors[plan] || "bg-gray-100"} border`} variant="outline">
      {labels[plan] || plan}
    </Badge>
  );
};

const SlotsGauge = ({ used, total }) => {
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const danger = used >= total;
  const warn = total - used <= 1 && !danger;
  const color = danger ? "bg-red-500" : warn ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{used} / {total} utilisateurs</span>
        <span className="text-muted-foreground">{pct}%</span>
      </div>
      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default function TeamBillingPage() {
  const { activeOrg, activeOrgId, isOrgAdmin, refresh, plans } = useOrg();
  const { locked, refresh: refreshLocked } = useLockedAccounts(isOrgAdmin);
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviteSeatType, setInviteSeatType] = useState("user"); // 'user' (15€) | 'worker' (5€)
  const [inviteEmployeeId, setInviteEmployeeId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState(null);

  const [buyOpen, setBuyOpen] = useState(false);
  const [slotsToBuy, setSlotsToBuy] = useState(1);
  const [buyWorkerOpen, setBuyWorkerOpen] = useState(false);
  const [workerSlotsToBuy, setWorkerSlotsToBuy] = useState(1);

  const [removeTarget, setRemoveTarget] = useState(null);
  const [permTarget, setPermTarget] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState("monthly");

  // Stripe checkout polling
  const [searchParams, setSearchParams] = useSearchParams();
  const [pollMessage, setPollMessage] = useState(null);
  const pollingRef = useRef(false);

  const fetchMembers = async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const res = await api.getOrgMembers(activeOrgId);
      setMembers(res.data?.members || []);
      setPendingInvites(res.data?.pending_invitations || []);
    } catch (e) {
      toast.error(getErrorMessage(e, "Impossible de charger les membres"));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchMembers(); }, [activeOrgId]);

  // Charge la liste des salariés actifs (pour le select de lien à l'invitation)
  useEffect(() => {
    if (!activeOrgId || !isOrgAdmin) return;
    api.getEmployees()
      .then((r) => setEmployees((r.data || []).filter((e) => !e.archived && !e.exit_date)))
      .catch(() => setEmployees([]));
  }, [activeOrgId, isOrgAdmin]);

  const handleInvite = async (e) => {
    e?.preventDefault?.();
    if (!inviteEmail.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.inviteOrgMember(activeOrgId, {
        email: inviteEmail.trim().toLowerCase(),
        org_role: inviteSeatType === "worker" ? "member" : inviteRole,
        seat_type: inviteSeatType,
        employee_id: inviteEmployeeId || null,
      });
      const link = `${window.location.origin}/accept-invite?token=${res.data.token}`;
      setLastInviteLink(link);
      if (res.data.email_sent) {
        toast.success(`Invitation envoyée à ${inviteEmail} par email`);
      } else {
        toast.warning(
          `Invitation créée mais l'email n'a pas pu être envoyé${res.data.email_error ? ` (${res.data.email_error})` : ""}. Partagez le lien manuellement.`,
        );
      }
      setInviteEmail("");
      setInviteEmployeeId("");
      setInviteSeatType("user");
      await fetchMembers();
      await refresh();
    } catch (err) {
      const msg = getErrorMessage(err, "Échec de l'invitation");
      toast.error(msg);
      // Si 402 (no slots), proposer d'en acheter
      if (err?.response?.status === 402) {
        setInviteOpen(false);
        setBuyOpen(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleBuySlots = async () => {
    setSubmitting(true);
    api.trackEvent("clicked_pay_with_stripe", {
      slots: parseInt(slotsToBuy, 10),
      billing_period: billingPeriod,
    }, activeOrgId);
    try {
      const res = await api.createSlotsCheckout(
        activeOrgId,
        parseInt(slotsToBuy, 10),
        window.location.origin,
        billingPeriod,
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error("Lien de paiement non reçu");
    } catch (err) {
      toast.error(getErrorMessage(err, "Achat d'utilisateurs impossible"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBuyWorkerSlots = async () => {
    setSubmitting(true);
    api.trackEvent("clicked_pay_with_stripe", {
      slots: parseInt(workerSlotsToBuy, 10),
      seat_type: "worker",
    }, activeOrgId);
    try {
      const res = await api.createWorkerSlotsCheckout(
        activeOrgId,
        parseInt(workerSlotsToBuy, 10),
        window.location.origin,
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error("Lien de paiement non reçu");
    } catch (err) {
      toast.error(getErrorMessage(err, "Achat d'ouvriers impossible"));
    } finally {
      setSubmitting(false);
    }
  };

  // Track when user opens the buy-slots dialog
  useEffect(() => {
    if (buyOpen) {
      api.trackEvent("view_buy_slots_dialog", {}, activeOrgId);
    }
  }, [buyOpen, activeOrgId]);

  // Poll the session status when coming back from Stripe (?session_id=...)
  useEffect(() => {
    const sid = searchParams.get("session_id");
    if (!sid || pollingRef.current) return;
    pollingRef.current = true;
    setPollMessage("Vérification du paiement…");

    let attempts = 0;
    const MAX_ATTEMPTS = 8;
    const INTERVAL_MS = 2000;

    const poll = async () => {
      attempts += 1;
      try {
        const res = await api.getCheckoutStatus(sid);
        const data = res.data || {};
        if (data.payment_status === "paid") {
          setPollMessage(null);
          toast.success(`Paiement validé — ${data.slots} utilisateur(s) ajouté(s) !`);
          api.trackEvent("payment_succeeded", {
            slots: data.slots,
            amount: data.amount_total ? data.amount_total / 100 : null,
            billing_period: data.billing_period,
          }, activeOrgId);
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
          await refresh();
          await fetchMembers();
          pollingRef.current = false;
          return;
        }
        if (data.status === "expired" || data.status === "canceled") {
          setPollMessage(null);
          toast.error("Session de paiement expirée ou annulée");
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
          pollingRef.current = false;
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setPollMessage(null);
          toast.warning("Délai dépassé — actualisez si le paiement n'apparaît pas");
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
          pollingRef.current = false;
          return;
        }
        setTimeout(poll, INTERVAL_MS);
      } catch (err) {
        setPollMessage(null);
        toast.error(getErrorMessage(err, "Vérification du paiement impossible"));
        searchParams.delete("session_id");
        setSearchParams(searchParams, { replace: true });
        pollingRef.current = false;
      }
    };
    poll();
  }, [searchParams, setSearchParams, refresh]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleRole = async (m) => {
    if (m.user_id === undefined) return;
    const newRole = m.org_role === "admin" ? "member" : "admin";
    if (!window.confirm(
      newRole === "admin"
        ? `Promouvoir ${m.email} en administrateur de l'organisation ?`
        : `Rétrograder ${m.email} en membre simple ?`
    )) return;
    try {
      await api.updateOrgMember(activeOrgId, m.user_id, { org_role: newRole });
      toast.success(`Rôle modifié`);
      await fetchMembers();
    } catch (err) {
      toast.error(getErrorMessage(err, "Modification impossible"));
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    try {
      await api.removeOrgMember(activeOrgId, removeTarget.user_id);
      toast.success(`${removeTarget.email} a été retiré de l'organisation`);
      setRemoveTarget(null);
      await fetchMembers();
      await refresh();
    } catch (err) {
      toast.error(getErrorMessage(err, "Retrait impossible"));
    }
  };

  const copyInvite = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Lien copié");
    } catch {
      toast.error("Impossible de copier");
    }
  };

  if (!activeOrg) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Chargement de l'organisation…
      </div>
    );
  }

  // Comptes utilisateurs (15€/mois) — séparés des comptes ouvriers (5€/mois)
  const userMembers = members.filter((m) => (m.seat_type || "user") !== "worker");
  const workerMembers = members.filter((m) => m.seat_type === "worker");
  const userPendingInvites = pendingInvites.filter((i) => (i.seat_type || "user") !== "worker");
  const workerPendingInvites = pendingInvites.filter((i) => i.seat_type === "worker");
  const slotsUsed = userMembers.length + userPendingInvites.length;
  const slotsTotal = activeOrg.slots_purchased || 1;
  const slotsLeft = Math.max(0, slotsTotal - slotsUsed);
  const workerSlotsUsed = workerMembers.length + workerPendingInvites.length;
  const workerSlotsTotal = activeOrg.worker_slots_purchased || 0;
  const workerSlotsLeft = Math.max(0, workerSlotsTotal - workerSlotsUsed);
  const isUnlimited = activeOrg.plan === "unlimited";
  const starterPlan = plans?.starter || { extra_slot_eur: 15, max_slots: 10, worker_slot_eur: 5 };
  const workerSlotPriceEur = starterPlan.worker_slot_eur || 5;
  // Pour le dialog d'invitation : a-t-il assez de slots du bon type ?
  const noSlotForCurrentType = !isUnlimited && (
    inviteSeatType === "worker" ? workerSlotsLeft <= 0 : slotsLeft <= 0
  );

  return (
    <div className="space-y-6 max-w-5xl" data-testid="team-billing-page">
      {pollMessage && (
        <div
          className="p-3 rounded-md bg-blue-50 border border-blue-200 flex items-center gap-2 text-sm text-blue-800"
          data-testid="payment-poll-banner"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>{pollMessage}</span>
        </div>
      )}
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-2">
            <Building2 className="h-8 w-8 text-primary" />
            Équipe & Facturation
          </h1>
          <p className="text-muted-foreground mt-1">
            Gérez les membres de votre organisation et leurs accès.
          </p>
        </div>
      </div>

      {/* Org card */}
      <Card data-testid="org-card">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                {activeOrg.name}
                <PlanBadge plan={activeOrg.plan} />
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {activeOrg.billing_email || "Email facturation non défini"}
              </p>
            </div>
            {isOrgAdmin && !isUnlimited && (
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBuyOpen(true)}
                  data-testid="open-buy-slots"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Utilisateurs ({starterPlan.extra_slot_eur}€)
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  onClick={() => setBuyWorkerOpen(true)}
                  data-testid="open-buy-worker-slots"
                >
                  <HardHat className="h-4 w-4 mr-2" />
                  Ouvriers ({workerSlotPriceEur}€)
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isUnlimited ? (
            <div
              className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900"
              data-testid="unlimited-banner"
            >
              <strong>Plan Gratuit illimité</strong> — Vous pouvez ajouter autant d'utilisateurs que vous le souhaitez, sans abonnement. Plan accordé manuellement par l'équipe Bati Assistant.
            </div>
          ) : (
            <SlotsGauge used={slotsUsed} total={slotsTotal} />
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <div className="p-3 rounded-md bg-muted/40">
              <p className="text-2xl font-bold">{userMembers.length}</p>
              <p className="text-xs text-muted-foreground">Utilisateurs actifs</p>
            </div>
            <div className="p-3 rounded-md bg-muted/40">
              <p className="text-2xl font-bold">{userPendingInvites.length}</p>
              <p className="text-xs text-muted-foreground">Invitations en attente</p>
            </div>
            <div className="p-3 rounded-md bg-muted/40">
              <p className="text-2xl font-bold">{isUnlimited ? "∞" : slotsLeft}</p>
              <p className="text-xs text-muted-foreground">Utilisateurs libres</p>
            </div>
            <div className="p-3 rounded-md bg-muted/40">
              <p className="text-2xl font-bold">{isUnlimited ? "0€" : `${starterPlan.extra_slot_eur}€`}</p>
              <p className="text-xs text-muted-foreground">/ utilisateur suppl.</p>
            </div>
          </div>

          {/* Comptes ouvriers (5€/mois — accès limité) */}
          {!isUnlimited && (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/40 p-4 space-y-3" data-testid="worker-card">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <HardHat className="h-5 w-5 text-emerald-700" />
                  <h3 className="font-semibold text-emerald-900">Comptes ouvriers</h3>
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[11px]" variant="outline">
                    {workerSlotPriceEur}€/mois — accès limité
                  </Badge>
                </div>
                <span className="text-xs text-emerald-700">
                  Demandes chantier + Fiches salariés uniquement
                </span>
              </div>
              <SlotsGauge used={workerSlotsUsed} total={workerSlotsTotal} />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-center text-sm">
                <div className="p-2 rounded bg-white">
                  <p className="text-xl font-bold text-emerald-700">{workerMembers.length}</p>
                  <p className="text-[11px] text-muted-foreground">Ouvriers actifs</p>
                </div>
                <div className="p-2 rounded bg-white">
                  <p className="text-xl font-bold text-emerald-700">{workerPendingInvites.length}</p>
                  <p className="text-[11px] text-muted-foreground">Invitations en attente</p>
                </div>
                <div className="p-2 rounded bg-white">
                  <p className="text-xl font-bold text-emerald-700">{workerSlotsLeft}</p>
                  <p className="text-[11px] text-muted-foreground">Sièges ouvriers libres</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Members card */}
      <Card data-testid="members-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Membres ({members.length})
          </CardTitle>
          {isOrgAdmin && (
            <Button
              size="sm"
              onClick={() => { setLastInviteLink(null); setInviteOpen(true); }}
              data-testid="open-invite-dialog"
              disabled={!isUnlimited && slotsLeft <= 0 && workerSlotsLeft <= 0}
              title={!isUnlimited && slotsLeft <= 0 && workerSlotsLeft <= 0 ? "Aucun siège disponible — achetez-en avant d'inviter" : ""}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Inviter
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Chargement…
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-2 font-medium">Email</th>
                    <th className="py-2 pr-2 font-medium">Nom</th>
                    <th className="py-2 pr-2 font-medium">Rôle</th>
                    <th className="py-2 pr-2 font-medium">Salarié lié</th>
                    <th className="py-2 pr-2 font-medium">Depuis</th>
                    {isOrgAdmin && <th className="py-2 pr-2 font-medium text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.user_id} className="border-b hover:bg-muted/30" data-testid={`member-row-${m.user_id}`}>
                      <td className="py-2 pr-2 font-medium">
                        {m.email}
                        {locked[m.user_id] && <LockedBadge lock={locked[m.user_id]} userId={m.user_id} />}
                      </td>
                      <td className="py-2 pr-2 text-muted-foreground">
                        {[m.first_name, m.last_name].filter(Boolean).join(" ") || "—"}
                      </td>
                      <td className="py-2 pr-2">
                        {m.org_role === "admin" ? (
                          <Badge className="bg-indigo-100 text-indigo-800 border-indigo-300 border" variant="outline">
                            <Crown className="h-3 w-3 mr-1" /> Admin
                          </Badge>
                        ) : m.seat_type === "worker" ? (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 border" variant="outline">
                            <HardHat className="h-3 w-3 mr-1" /> Ouvrier
                          </Badge>
                        ) : (
                          <Badge variant="outline">Membre</Badge>
                        )}
                      </td>
                      <td className="py-2 pr-2 text-xs" data-testid={`member-employee-${m.user_id}`}>
                        {m.employee_name ? (
                          <Badge className="bg-emerald-50 text-emerald-800 border-emerald-300 border" variant="outline">
                            <Link2 className="h-3 w-3 mr-1" /> {m.employee_name}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-2 pr-2 text-muted-foreground text-xs">
                        {m.joined_at?.slice(0, 10) || "—"}
                      </td>
                      {isOrgAdmin && (
                        <td className="py-2 pr-2 text-right">
                          <div className="inline-flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              title="Modifier les permissions"
                              onClick={() => setPermTarget(m)}
                              data-testid={`edit-perms-${m.user_id}`}
                            >
                              <Settings className="h-4 w-4 text-indigo-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              title={m.org_role === "admin" ? "Rétrograder en membre" : "Promouvoir admin"}
                              onClick={() => handleToggleRole(m)}
                              data-testid={`toggle-role-${m.user_id}`}
                            >
                              <ShieldCheck className={`h-4 w-4 ${m.org_role === "admin" ? "text-amber-600" : "text-green-600"}`} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-600 hover:bg-red-50"
                              title="Retirer ce membre"
                              onClick={() => setRemoveTarget(m)}
                              data-testid={`remove-member-${m.user_id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            {locked[m.user_id] ? (
                              <UnlockButton userId={m.user_id} email={m.email} onDone={refreshLocked} />
                            ) : (
                              <span className="inline-block w-8" aria-hidden="true" />
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending invitations */}
      {pendingInvites.length > 0 && (
        <Card data-testid="pending-invites-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Mail className="h-5 w-5 text-amber-600" />
              Invitations en attente ({pendingInvites.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingInvites.map((inv) => {
                const invLink = inv.token ? `${window.location.origin}/accept-invite?token=${inv.token}` : null;
                return (
                  <div key={inv.id} className="flex flex-wrap items-center justify-between gap-2 p-2 rounded-md bg-amber-50 border border-amber-200" data-testid={`pending-invite-${inv.id}`}>
                    <div className="flex items-center gap-2 text-sm min-w-0">
                      <Mail className="h-4 w-4 text-amber-700 shrink-0" />
                      <span className="font-medium truncate">{inv.email}</span>
                      <Badge variant="outline" className="text-xs">{inv.org_role}</Badge>
                      {inv.resend_count > 0 && (
                        <Badge variant="outline" className="text-[10px] border-slate-300">Relancé {inv.resend_count}×</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-muted-foreground mr-2">
                        Expire {inv.expires_at?.slice(0, 10)}
                      </span>
                      {isOrgAdmin && invLink && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              navigator.clipboard.writeText(invLink);
                              toast.success("Lien d'invitation copié");
                            }}
                            title="Copier le lien d'activation"
                            data-testid={`copy-invite-${inv.id}`}
                          >
                            <Link2 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={async () => {
                              try {
                                await api.resendOrgInvitation(activeOrgId, inv.id);
                                toast.success(`Email renvoyé à ${inv.email}`);
                                await fetchMembers();
                              } catch (e) {
                                toast.error(getErrorMessage(e, "Échec du renvoi"));
                              }
                            }}
                            title="Renvoyer l'email d'invitation"
                            data-testid={`resend-invite-${inv.id}`}
                          >
                            <Send className="h-4 w-4 text-indigo-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={async () => {
                              if (!confirm(`Révoquer l'invitation pour ${inv.email} ?`)) return;
                              try {
                                await api.deleteOrgInvitation(activeOrgId, inv.id);
                                toast.success("Invitation révoquée");
                                await fetchMembers();
                                await refresh();
                              } catch (e) {
                                toast.error(getErrorMessage(e, "Échec de la suppression"));
                              }
                            }}
                            title="Révoquer l'invitation"
                            data-testid={`delete-invite-${inv.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={(o) => { setInviteOpen(o); if (!o) setLastInviteLink(null); }}>
        <DialogContent data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle>Inviter un membre</DialogTitle>
            <DialogDescription>
              Un lien d'invitation sera généré (valable 7 jours).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                required
                placeholder="prenom.nom@entreprise.fr"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                data-testid="invite-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Type de siège</Label>
              <Select value={inviteSeatType} onValueChange={setInviteSeatType}>
                <SelectTrigger data-testid="invite-seat-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">
                    Utilisateur ({starterPlan.extra_slot_eur}€/mois) — accès configurable
                  </SelectItem>
                  <SelectItem value="worker">
                    Ouvrier ({workerSlotPriceEur}€/mois) — Demandes chantier + Fiches salariés uniquement
                  </SelectItem>
                </SelectContent>
              </Select>
              {inviteSeatType === "worker" && (
                <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5">
                  Ce compte aura uniquement accès à <strong>Demandes chantier</strong> et
                  <strong> Fiches salariés</strong>. Idéal pour les ouvriers terrain.
                </p>
              )}
              {!isUnlimited && noSlotForCurrentType && (
                <p className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
                  Aucun siège <strong>{inviteSeatType === "worker" ? "ouvrier" : "utilisateur"}</strong> disponible.
                  Achetez-en un avant d'inviter.
                </p>
              )}
            </div>

            {inviteSeatType !== "worker" && (
              <div className="space-y-2">
                <Label>Rôle</Label>
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger data-testid="invite-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Membre (aucun accès par défaut)</SelectItem>
                    <SelectItem value="admin">Administrateur</SelectItem>
                  </SelectContent>
                </Select>
                {inviteRole === "member" && (
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                    Par défaut, ce membre n'aura accès à <strong>aucun module</strong>.
                    Vous pourrez débloquer les modules dans la fiche membre.
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="invite-employee">Lier à un salarié <span className="text-muted-foreground font-normal">(optionnel)</span></Label>
              <Select value={inviteEmployeeId || "__none__"} onValueChange={(v) => setInviteEmployeeId(v === "__none__" ? "" : v)}>
                <SelectTrigger id="invite-employee" data-testid="invite-employee-select">
                  <SelectValue placeholder="Aucun lien" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Aucun (utilisateur non lié)</SelectItem>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {inviteEmployeeId && (
                <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5">
                  Ce membre pourra <strong>uniquement</strong> pointer ses heures et soumettre ses
                  absences pour le salarié sélectionné.
                </p>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {inviteSeatType === "worker"
                ? <>Ouvriers restants : <strong>{workerSlotsLeft}</strong> / {workerSlotsTotal}</>
                : <>Utilisateurs restants : <strong>{slotsLeft}</strong> / {slotsTotal}</>
              }
            </p>
            {lastInviteLink && (
              <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-sm space-y-2">
                <p className="text-emerald-800 font-medium">Lien d'invitation prêt :</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs break-all bg-white px-2 py-1 rounded border">
                    {lastInviteLink}
                  </code>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="h-8 w-8"
                    onClick={() => copyInvite(lastInviteLink)}
                    data-testid="copy-invite-link"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-emerald-700">
                  Envoyez ce lien à votre collaborateur — il pourra créer son mot de passe et rejoindre l'organisation.
                </p>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>
                Fermer
              </Button>
              <Button
                type="submit"
                disabled={submitting || !inviteEmail || (!isUnlimited && noSlotForCurrentType)}
                data-testid="submit-invite"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Inviter
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Buy slots dialog */}
      <Dialog open={buyOpen} onOpenChange={setBuyOpen}>
        <DialogContent data-testid="buy-slots-dialog">
          <DialogHeader>
            <DialogTitle>Acheter des utilisateurs</DialogTitle>
            <DialogDescription>
              Plan Starter : 79€/mois inclut 1 utilisateur. Utilisateurs supplémentaires : {starterPlan.extra_slot_eur}€/utilisateur/mois (max {starterPlan.max_slots || 10}).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Billing period toggle */}
            <div className="space-y-2">
              <Label>Période de facturation</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="billing-period-toggle">
                <button
                  type="button"
                  onClick={() => setBillingPeriod("monthly")}
                  className={`p-3 rounded-md border-2 text-left transition-all ${
                    billingPeriod === "monthly"
                      ? "border-indigo-500 bg-indigo-50"
                      : "border-border bg-card hover:border-indigo-300"
                  }`}
                  data-testid="billing-period-monthly"
                >
                  <div className="text-sm font-semibold">Mensuel</div>
                  <div className="text-xs text-muted-foreground">{starterPlan.extra_slot_eur}€/utilisateur/mois</div>
                </button>
                <button
                  type="button"
                  onClick={() => setBillingPeriod("annual")}
                  className={`p-3 rounded-md border-2 text-left transition-all relative ${
                    billingPeriod === "annual"
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-border bg-card hover:border-emerald-300"
                  }`}
                  data-testid="billing-period-annual"
                >
                  <span className="absolute top-1 right-1 text-[10px] font-bold bg-emerald-600 text-white px-1.5 py-0.5 rounded">
                    -10%
                  </span>
                  <div className="text-sm font-semibold">Annuel</div>
                  <div className="text-xs text-muted-foreground">
                    {(starterPlan.extra_slot_eur * 12 * 0.9).toFixed(0)}€/utilisateur/an
                  </div>
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="slots-input">Nombre d'utilisateurs supplémentaires</Label>
              <Input
                id="slots-input"
                type="number"
                min={1}
                max={9}
                value={slotsToBuy}
                onChange={(e) => setSlotsToBuy(e.target.value)}
                data-testid="slots-input"
              />
            </div>
            <div className="p-3 rounded-md bg-indigo-50 border border-indigo-200 text-sm">
              {(() => {
                const slots = parseInt(slotsToBuy, 10) || 0;
                const unitMonthly = starterPlan.extra_slot_eur;
                const unitAnnual = unitMonthly * 12 * 0.9;
                if (billingPeriod === "annual") {
                  const total = slots * unitAnnual;
                  const saved = slots * unitMonthly * 12 - total;
                  return (
                    <>
                      <p>Total : <strong>{total.toFixed(0)}€</strong> / an</p>
                      <p className="text-xs text-emerald-700 font-medium mt-1">
                        🎉 Vous économisez {saved.toFixed(0)}€/an grâce au paiement annuel.
                      </p>
                    </>
                  );
                }
                return (
                  <>
                    <p>Total : <strong>{(slots * unitMonthly).toFixed(0)}€</strong> / mois</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Astuce : passez en annuel pour économiser 10% (= {(slots * unitMonthly * 12 * 0.1).toFixed(0)}€/an).
                    </p>
                  </>
                );
              })()}
              <p className="text-xs text-muted-foreground mt-2">
                Vous serez redirigé vers Stripe pour payer en toute sécurité. Carte test : <code className="bg-white px-1 rounded">4242 4242 4242 4242</code>
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBuyOpen(false)}>Annuler</Button>
              <Button onClick={handleBuySlots} disabled={submitting} data-testid="confirm-buy-slots">
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Payer avec Stripe
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Buy worker slots dialog */}
      <Dialog open={buyWorkerOpen} onOpenChange={setBuyWorkerOpen}>
        <DialogContent data-testid="buy-worker-slots-dialog">
          <DialogHeader>
            <DialogTitle>Acheter des comptes ouvriers</DialogTitle>
            <DialogDescription>
              {workerSlotPriceEur}€ HT par compte / mois — accès limité à
              <strong> Demandes chantier</strong> et <strong>Fiches salariés</strong>.
              Idéal pour les ouvriers terrain qui pointent leurs heures et signalent les
              demandes matériel.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="worker-slots-input">Nombre de comptes ouvriers à ajouter</Label>
              <Input
                id="worker-slots-input"
                type="number"
                min="1"
                max="50"
                value={workerSlotsToBuy}
                onChange={(e) => setWorkerSlotsToBuy(parseInt(e.target.value) || 1)}
                data-testid="worker-slots-input"
              />
            </div>
            <div className="rounded-md border bg-emerald-50/50 border-emerald-200 p-3 space-y-1 text-sm">
              <p className="flex justify-between">
                <span>Prix unitaire</span>
                <span className="font-medium">{workerSlotPriceEur}€ HT / mois</span>
              </p>
              <p className="flex justify-between">
                <span>Quantité</span>
                <span className="font-medium">× {workerSlotsToBuy}</span>
              </p>
              <hr className="border-emerald-200" />
              <p className="flex justify-between font-semibold text-emerald-900">
                <span>Total à payer</span>
                <span>{(workerSlotsToBuy * workerSlotPriceEur).toFixed(2)}€ HT</span>
              </p>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Vous serez redirigé vers Stripe pour finaliser le paiement. Les comptes seront
              débloqués automatiquement après confirmation.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBuyWorkerOpen(false)}>Annuler</Button>
              <Button
                onClick={handleBuyWorkerSlots}
                disabled={submitting || workerSlotsToBuy < 1}
                data-testid="confirm-buy-worker-slots"
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Payer avec Stripe ({(workerSlotsToBuy * workerSlotPriceEur).toFixed(2)}€)
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove member alert */}
      <AlertDialog open={!!removeTarget} onOpenChange={(o) => { if (!o) setRemoveTarget(null); }}>        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retirer ce membre ?</AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget?.email} perdra immédiatement l'accès à l'organisation.
              Ses données restent attribuées à l'organisation (les commandes, clients, etc. ne sont pas supprimés).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemove}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-remove-member"
            >
              Retirer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permissions editor dialog */}
      <PermissionsEditorDialog
        open={!!permTarget}
        member={permTarget}
        onClose={() => setPermTarget(null)}
        onSaved={() => { fetchMembers(); refresh(); }}
      />
    </div>
  );
}
